import auth0 from 'auth0-js';
import { EventEmitter } from 'events';
import { config } from '@/config';

const webAuth = new auth0.WebAuth({
  domain: config.auth.auth0Domain,
  redirectUri: `${window.location.origin}/callback`,
  clientID: config.auth.auth0ClientId,
  responseType: 'id_token',
  scope: 'openid profile email',
});

const localStorageKey = 'loggedIn';
const loginEvent = 'loginEvent';
const localStorageKeyAuth = 'auth';

class AuthService extends EventEmitter {
  idToken = null;
  profile = null;
  tokenExpiry = null;

  login(customState) {
    webAuth.authorize({
      appState: customState,
    });
  }

  logOut() {
    localStorage.removeItem(localStorageKey);
    localStorage.removeItem(localStorageKeyAuth);

    this.idToken = null;
    this.tokenExpiry = null;
    this.profile = null;

    webAuth.logout({
      returnTo: `${window.location.origin}`,
    });

    this.emit(loginEvent, { loggedIn: false });
  }

  handleAuthentication() {
    return new Promise((resolve, reject) => {
      webAuth.parseHash((err, authResult) => {
        if (err) {
          reject(err);
        } else {
          this.localLogin(authResult);
          resolve(authResult.idToken);
        }
      });
    });
  }

  isAuthenticated() {
    if (this.tokenExpiry === null) {
      this.setTokensFromLocalStorage();
    }
    return (
      Date.now() < this.tokenExpiry &&
      localStorage.getItem(localStorageKey) === 'true'
    );
  }

  isIdTokenValid() {
    if (this.tokenExpiry === null) {
      this.setTokensFromLocalStorage();
    }
    return (
      this.idToken &&
      this.tokenExpiry &&
      Date.now() < this.tokenExpiry
    );
  }

  getIdToken() {
    return new Promise((resolve, reject) => {
      if (this.isIdTokenValid()) {
        resolve(this.idToken);
      } else if (this.isAuthenticated()) {
        this.renewTokens().then(authResult => {
          resolve(authResult.idToken);
        }, reject);
      } else {
        resolve();
      }
    });
  }

  localLogin(authResult) {
    this.idToken = authResult.idToken;
    this.profile = authResult.idTokenPayload;

    // Convert the expiry time from seconds to milliseconds,
    // required by the Date constructor
    this.tokenExpiry = new Date(this.profile.exp * 1000);

    // Hvis token er udløbet, så stop
    if (this.tokenExpiry < Date.now()) {
      this.emit(loginEvent, { loggedIn: false });
      return false;
    }

    localStorage.setItem(localStorageKey, 'true');
    localStorage.setItem(localStorageKeyAuth, JSON.stringify(authResult));

    this.emit(loginEvent, {
      loggedIn: true,
      idToken: authResult.idToken,
      profile: authResult.idTokenPayload,
      state: authResult.appState || {},
    });
  }

  renewTokens() {
    return new Promise((resolve, reject) => {
      if (localStorage.getItem(localStorageKey) !== 'true') {
        // eslint-disable-next-line prefer-promise-reject-errors
        return reject('Not logged in');
      }
      const authResult = this.getAuthResultFromLocalStorage();
      if (authResult === null) {
        // eslint-disable-next-line prefer-promise-reject-errors
        reject('Not logged in');
      } else {
        this.localLogin(authResult);
        resolve(authResult);
      }
      // Original auth0 code
      // webAuth.checkSession({}, (err, authResult) => {
      //   if (err) {
      //     reject(err);
      //   } else {
      //     this.localLogin(authResult);
      //     resolve(authResult);
      //   }
      // });
    });
  }

  getAuthResultFromLocalStorage() {
    return JSON.parse(localStorage.getItem(localStorageKeyAuth));
  }

  setTokensFromLocalStorage() {
    const authResult = this.getAuthResultFromLocalStorage();
    if (authResult !== null) {
      this.idToken = authResult.idToken;
      this.profile = authResult.idTokenPayload;
      // Convert the expiry time from seconds to milliseconds,
      // required by the Date constructor
      this.tokenExpiry = new Date(this.profile.exp * 1000);
    }
    return authResult;
  }
}

const service = new AuthService();

service.setMaxListeners(5);

export default service;
